import { useEffect, useMemo } from "react";

// Import Poppins font
import "@fontsource/poppins/400.css"; // Specify weight
import "@fontsource/poppins/500.css";
import "@fontsource/poppins/600.css";
import "@fontsource/poppins/400-italic.css";
import "@fontsource/poppins/700.css";
import Icon from "../icon/Icon";
import { Navigate, Outlet, useParams } from "react-router";
import { useCompanyBrandingQuery } from "../../common/queries";
import { useAuth } from "../authentication/useAuth";
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES_TYPE } from "../../i18n";
import { getLanguageCode } from "../../helpers/translateHelpers";
import {
  defaultPrimaryAccentColor,
  defaultPrimaryColor,
} from "../../app_config";
import {
  VisualProviderContext,
  VisualProviderContextType,
} from "./useVisualProvider";

type VisualParams = "tenantCode";

const VisualProvider = () => {
  const { setLanguage } = useAuth();

  const params = useParams<VisualParams>();

  const companyBranding = useCompanyBrandingQuery(params.tenantCode);

  useEffect(() => {
    if (params?.tenantCode) {
      if (companyBranding.data?.accentColor)
        document.documentElement.style.setProperty(
          "--color-primary-accent",
          companyBranding.data.accentColor,
        );

      if (companyBranding.data?.primaryColor)
        document.documentElement.style.setProperty(
          "--color-primary",
          companyBranding.data.primaryColor,
        );
    } else {
      document.documentElement.style.setProperty(
        "--color-primary-accent",
        defaultPrimaryAccentColor,
      );

      document.documentElement.style.setProperty(
        "--color-primary",
        defaultPrimaryColor,
      );
    }

    setLanguage(
      getLanguageCode() ??
        (companyBranding.data?.languageId as SUPPORTED_LANGUAGES_TYPE) ??
        DEFAULT_LANGUAGE,
    );
  }, [companyBranding.data, setLanguage, params]);

  const memoedValue = useMemo<VisualProviderContextType>(
    () => ({
      accentColor:
        companyBranding.data?.accentColor ?? defaultPrimaryAccentColor,
      primaryColor: companyBranding.data?.primaryColor ?? defaultPrimaryColor,
      companyName: companyBranding.data?.companyName ?? "",
      logoUrl: companyBranding.data?.logoUrl ?? "",
    }),
    [companyBranding.data],
  );

  if (companyBranding.error) {
    return <Navigate to={"/"} />;
  }

  return (
    <VisualProviderContext.Provider value={memoedValue}>
      {companyBranding.isLoading ? (
        <div className="absolute top-1/2 m-0 flex w-full animate-spin justify-center">
          <Icon name="loading" />
        </div>
      ) : (
        <Outlet />
      )}
    </VisualProviderContext.Provider>
  );
};

export default VisualProvider;
